import { useCallback, useEffect, useState } from 'react';
import {
  CHAT_DRAWER_MAX_WIDTH,
  CHAT_DRAWER_MIN_WIDTH,
  chatDrawerDefaultWidth,
} from '@wundo/ui';

/*
 * Versioned, and the version before it is not read. The old key held a width
 * that was written whether or not anyone had asked for it, so a drawer nobody
 * ever touched would hold the old default for ever and never see a new one.
 */
const STORAGE_KEY = 'wundo.chat.width.v2';
const LEGACY_KEY = 'wundo.chat.width';

/* A width remembered from a wider screen is still a width, but not any width. */
function clamp(width: number): number {
  return Math.min(Math.max(width, CHAT_DRAWER_MIN_WIDTH), CHAT_DRAWER_MAX_WIDTH);
}

/*
 * Same treatment as the rail: reading storage can throw outright in a
 * browser that blocks site data, and a drawer that fails to render is worse
 * than one that forgets how wide it was.
 */
function readStored(): number | null {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? clamp(stored) : null;
  } catch {
    return null;
  }
}

function writeStored(width: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    /* The drawer still resizes; it just forgets between visits. */
  }
}

function clearStored(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nothing to do about it, and nothing that stops the drawer working. */
  }
}

/* The key nothing reads any more. Taken out so it does not sit there for ever. */
function forgetLegacyWidth(): void {
  try {
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* See above. */
  }
}

/**
 * The width the user asked for, whether or not anything is drawing the drawer.
 *
 * Not what ends up on screen — that is `chatDrawerFit`'s answer, which caps
 * this by the room the page can spare.
 */
export function chatDrawerWidth(): number {
  return readStored() ?? chatDrawerDefaultWidth();
}

/**
 * The width the user asked for, remembered across visits — and only theirs.
 *
 * Nothing is written until somebody drags the handle or steps it with a key.
 * A drawer nobody has sized follows whatever width the design opens at — which
 * is a share of this window, so a big screen opens wider than a laptop, and
 * changing that rule reaches everyone who never had an opinion about it;
 * putting it back (Home, or a double-click) is saying you have no opinion
 * again, and it stops being remembered.
 */
export function useChatDrawerWidth(): [number, (width: number) => void, () => void] {
  const [width, setWidth] = useState(chatDrawerWidth);

  useEffect(forgetLegacyWidth, []);

  const set = useCallback((next: number) => {
    const clamped = clamp(next);
    writeStored(clamped);
    setWidth(clamped);
  }, []);

  const reset = useCallback(() => {
    clearStored();
    setWidth(chatDrawerDefaultWidth());
  }, []);

  return [width, set, reset];
}
